import React, { useState, useMemo } from "react";
import { Table, Button, Row, Col, Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useParams } from "react-router-dom";
import { useGetProductsQuery } from "../../slices/productsApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";
import { useGetStoreStocksQuery } from "../../slices/storeStocksApiSlice";
import { useGetStoreSalesQuery } from "../../slices/storeSalesApiSlice";
import { useGetStoreTransfersQuery } from "../../slices/storeTransfersApiSlice";
import { useGetOpeningStoreStocksQuery } from "../../slices/openingStoreStocksApiSlice";

const ReportStoreStockScreen = () => {
  const [filter, setFilter] = useState("");

  // Queries
  const { pageNumber } = useParams();
  const { data: prods, isLoading: loadingP, error: errP } = useGetProductsQuery({ pageNumber });
  const { data: supp, isLoading: loadingS, error: errS } = useGetStoreSuppliesQuery();
  const { data: stk, isLoading: loadingStk, error: errStk } = useGetStoreStocksQuery();
  const { data: storeSales, isLoading: loadingStoreSale, error: errStoreSale } = useGetStoreSalesQuery();
  const { data: trans, isLoading: loadingT, error: errT } = useGetStoreTransfersQuery();
  const { data: opens, isLoading: loadingOS, error: errOS } = useGetOpeningStoreStocksQuery();

  const loading = loadingP || loadingS || loadingStk || loadingStoreSale || loadingT || loadingOS;
  const error = errP || errS || errStk || errStoreSale || errT || errOS;

  // Determine earliest storeStock date for storeStock calculations
  const oldestStoreStockDate = useMemo(() => {
    if (!stk?.storeStocks?.length) return "";
    return stk.storeStocks.reduce((oldest, s) => {
      return !oldest || new Date(s.date) < new Date(oldest) ? s.date : oldest;
    }, "");
  }, [stk]);

  // Compute mapped quantities
  const { openingMap, openingPriceMap, storeSupplyMap, storeStockMap, storeStockPriceMap, soldMap, storeTransferMap, priceMap } =
    useMemo(() => {
      const openingMap = new Map();
      const openingPriceMap = new Map();
      const storeSupplyMap = new Map();
      const storeStockMap = new Map();
      const storeStockPriceMap = new Map();
      const soldMap = new Map();
      const storeTransferMap = new Map();
      const priceMap = new Map();

      // Sum opening storeStock quantities and prices separately
      opens?.openingStoreStocks.forEach(os =>
        os.openingStoreStockItems.forEach(i => {
          openingMap.set(i.name, (openingMap.get(i.name) || 0) + i.qty);
          openingPriceMap.set(i.name, i.price);
        })
      );

      // Sum storeSupplies, storeSales, storeTransfers based on product name
      supp?.storeSupplies.forEach(s =>
        s.storeSupplyItems.forEach(i => storeSupplyMap.set(i.name, (storeSupplyMap.get(i.name) || 0) + i.qty))
      );
      storeSales?.storeSales.forEach(s =>
        s.storeSaleItems.forEach(i => soldMap.set(i.name, (soldMap.get(i.name) || 0) + i.qty))
      );
      trans?.storeTransfers.forEach(t =>
        t.storeTransferItems.forEach(i => storeTransferMap.set(i.name, (storeTransferMap.get(i.name) || 0) + i.qty))
      );

      // Get product prices (Cur Price)
      prods?.products.forEach(p => priceMap.set(p.name, p.price));

      // Filter storeStocks based on earliest storeStock date **AND** matching product name
      stk?.storeStocks
        .filter(s => s.date === oldestStoreStockDate)
        .forEach(s =>
          s.storeStockItems.forEach(i => {
            storeStockMap.set(i.name, (storeStockMap.get(i.name) || 0) + i.qty);
            storeStockPriceMap.set(i.name, i.price);
          })
        );

      return { openingMap, openingPriceMap, storeSupplyMap, storeStockMap, storeStockPriceMap, soldMap, storeTransferMap, priceMap };
    }, [opens, supp, stk, storeSales, trans, prods, oldestStoreStockDate]);

  // Generate report rows
  const reportRows = useMemo(() => {
    if (!prods?.products) return [];

    return prods.products
      .filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
      .map(prod => {
        const openingQty = openingMap.get(prod.name) || 0;
        const opPrice = openingPriceMap.get(prod.name) || 0;

        const stkQty = storeStockMap.get(prod.name) || 0;
        const stPrice = storeStockPriceMap.get(prod.name) || 0;

        const supQty = storeSupplyMap.get(prod.name) || 0;
        const soldQty = soldMap.get(prod.name) || 0;
        const trQty = storeTransferMap.get(prod.name) || 0;
        const balQty = stkQty + supQty - soldQty - trQty;

        const curPrice = priceMap.get(prod.name) || 0;
        const incPrice = curPrice - opPrice;
        const balVal = balQty * curPrice;
        const opVal = opPrice * openingQty;
        const stVal = stPrice * stkQty;

        return {
          _id: prod._id,
          name: prod.name,
          openingQty,
          opPrice,
          stkQty,
          stPrice,
          supQty,
          soldQty,
          trQty,
          balQty,
          curPrice,
          incPrice,
          balVal,
          opVal,
          stVal,
        };
      });
  }, [prods, filter, openingMap, openingPriceMap, storeSupplyMap, storeStockMap, storeStockPriceMap, soldMap, storeTransferMap, priceMap]);

  // Export to Excel
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportRows.map(r => ({
      Product: r.name,
      "Opening QTY": r.openingQty,
      "Op Price": r.opPrice,
      "StoreStock QTY": r.stkQty,
      "St Price": r.stPrice,
      "Supplied QTY": r.supQty,
      "Sold QTY": r.soldQty,
      "StoreTransferred QTY": r.trQty,
      "Balance QTY": r.balQty,
      "Cur Price": r.curPrice,
      "Inc Price": r.incPrice,
      "Balance Val": r.balVal.toFixed(2),
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StoreStock Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), "report_storeStock.xlsx");
  };
  // Grand Total of Balance Value
  const grandTotalBalanceValue = reportRows.reduce((sum, row) => sum + row.balVal, 0).toFixed(2);
  const grandTotalOpValue = reportRows.reduce((sum, row) => sum + row.opVal, 0).toFixed(2);
  const grandTotalStValue = reportRows.reduce((sum, row) => sum + row.stVal, 0).toFixed(2);


  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>Report MiniStock</h1></Col>
      </Row>
      {/* Grand Total */}
      <Row className="mb-3">
        <Col md={6}><h4>Balance Value: ${grandTotalBalanceValue}</h4></Col>
        <Col md={6}><h4>OPening Value: ${grandTotalOpValue}</h4></Col>
        <Col md={6}><h4>StoreStock Value: ${grandTotalStValue}</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col md={8}>
          <Form.Control type="text" placeholder="Filter by product" value={filter} onChange={e => setFilter(e.target.value)} />
        </Col>
        <Col md={4} className="text-end">
          <Button variant="success" onClick={handleExportExcel}>Export to Excel</Button>
        </Col>
      </Row>

      {!loading && !error && (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>ID</th>
              <th>Product</th>
              <th>Op QTY</th>
              <th>Op Price</th>
              <th>StoreStock QTY</th>
              <th>St Price</th>
              <th>Sup QTY</th>
              <th>Sold QTY</th>
              <th>Transf QTY</th>
              <th>Bal QTY</th>
              <th>Cur Price</th>
              <th>Inc Price</th>
              <th>Bal Val</th>
              <th>OP Val</th>
              <th>ST Val</th>
            </tr>
          </thead>
          <tbody>{reportRows.map(r => <tr key={r._id}>{Object.values(r).map((value, idx) => <td key={idx}>{value}</td>)}</tr>)}</tbody>
        </Table>
      )}
    </>
  );
};

export default ReportStoreStockScreen;
